import { useState, useMemo, useEffect, useCallback } from "react"
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Collapse,
  Divider,
  Tooltip,
  IconButton,
  CircularProgress,
  selectClasses,
} from "@mui/material"
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Comment as CommentIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material"
import { useApiQuery, useApiMutation } from "../../hooks/useApi"
import { useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import JobsTable from "../../components/jobs/JobsTable"
import JobDetailsDialog from "../../components/jobs/JobDetailsDialog"
import EditJobDialog from "../../components/jobs/EditJobDialog"
import * as XLSX from "xlsx"
import api from "../../utils/api"

function JobsPage({ status: initialStatus = "All" }) {
  const [searchTerm, setSearchTerm] = useState("")
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [selectedJob, setSelectedJob] = useState(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [addCommentDialogOpen, setAddCommentDialogOpen] = useState(false)
  const [comment, setComment] = useState("")
  const [isExporting, setIsExporting] = useState(false)

  // Pagination state (0-based for MUI, converted to 1-based for API)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  // Debounced text fields — separate state so API doesn't fire on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [debouncedFilters, setDebouncedFilters] = useState({
    status: initialStatus,
    subStatus: "All",
    assignedTo: "All",
    clientName: "",
    clientPan: "",
    jobId: "",
    jobType: "All",
  })

  // Advanced filter states (bound to inputs)
  const [filters, setFilters] = useState({
    status: initialStatus,
    subStatus: "All",
    assignedTo: "All",
    clientName: "",
    clientPan: "",
    jobId: "",
    jobType: "All",
  })

  // Debounce text-based filters (clientName, clientPan, jobId, search) 450ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setDebouncedFilters(filters)
      setPage(0)  // reset page when filter text changes
    }, 700)
    return () => clearTimeout(timer)
  }, [searchTerm, filters.clientName, filters.clientPan, filters.jobId])

  // Dropdown filters fire immediately, but still reset page
  useEffect(() => {
    setDebouncedFilters((prev) => ({
      ...prev,
      status: filters.status,
      subStatus: filters.subStatus,
      assignedTo: filters.assignedTo,
      jobType: filters.jobType,
    }))
    setPage(0)
  }, [filters.status, filters.subStatus, filters.assignedTo, filters.jobType])

  // Build the query string used by React Query
  const queryParams = useMemo(() => {
    const p = new URLSearchParams()
    p.set("page", String(page + 1))
    p.set("limit", String(rowsPerPage))
    if (debouncedFilters.status !== "All") p.set("status", debouncedFilters.status)
    if (debouncedFilters.subStatus !== "All") p.set("subStatus", debouncedFilters.subStatus)
    if (debouncedFilters.assignedTo !== "All") p.set("assignedTo", debouncedFilters.assignedTo)
    if (debouncedFilters.jobType !== "All") p.set("jobType", debouncedFilters.jobType)
    if (debouncedFilters.jobId) p.set("jobId", debouncedFilters.jobId)
    if (debouncedFilters.clientName) p.set("clientName", debouncedFilters.clientName)
    if (debouncedFilters.clientPan) p.set("clientPan", debouncedFilters.clientPan)
    if (debouncedSearch) p.set("search", debouncedSearch)
    return p.toString()
  }, [debouncedFilters, debouncedSearch, page, rowsPerPage])

  const queryClient = useQueryClient()

  // Use paginated endpoint — React Query re-fetches automatically when queryParams changes
  const { data: result, isLoading, refetch } = useApiQuery(
    ["jobs", queryParams],
    `/jobs/paginated?${queryParams}`
  )
  const { data: users } = useApiQuery(["users"], "/users")
  const { data: jobList } = useApiQuery(["jobTypes"], "/job-list")

  const updateJobMutation = useApiMutation(
    `/jobs/update/${selectedJob?.JobId}`,
    "PATCH",
    {
      onSuccess: () => {
        setEditDialogOpen(false)
        refetch()
      },
    }
  )

  const setJobCommentMutation = useApiMutation(
    `/jobs/${selectedJob?.JobId}/comments`,
    "POST",
    {
      onSuccess: () => {
        setAddCommentDialogOpen(false)
        setComment("")
        queryClient.invalidateQueries({ queryKey: "jobs" })
      },
    }
  )

  const STATUSES = ["new", "processing", "completed", "cancelled", "closed"]
  const SUB_STATUSES = [
    "documentsPending",
    "taxPending",
    "verificationPending",
    "clientNotResponding",
    "keptOnHold",
  ]

  const getSubStatusLabel = (subStatus) => {
    const labels = {
      documentsPending: "Documents Pending",
      taxPending: "Tax Pending",
      verificationPending: "Verification Pending",
      clientNotResponding: "Client Not Responding",
      keptOnHold: "Kept On Hold",
    }
    return labels[subStatus] || subStatus
  }

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
    // Dropdown changes reset page immediately via the useEffect
  }

  const handleClearFilters = () => {
    setFilters({
      status: initialStatus,
      subStatus: "All",
      assignedTo: "All",
      clientName: "",
      clientPan: "",
      jobId: "",
      jobType: "All",
    })
    setSearchTerm("")
    setPage(0)
  }

  const getActiveFilterCount = () => {
    let count = 0
    if (filters.status !== "All" && initialStatus === "All") count++
    if (filters.subStatus !== "All") count++
    if (filters.assignedTo !== "All") count++
    if (filters.clientName) count++
    if (filters.clientPan) count++
    if (filters.jobId) count++
    if (filters.jobType !== "All") count++
    if (searchTerm) count++
    return count
  }

  // Jobs from the paginated API response
  const jobs = result?.jobs || []
  const totalJobs = result?.total ?? 0

  // Define table columns with custom render for assignedTo
  const columns = [
    { id: "jobId", label: "Job ID", field: "JobId", type: "jobId" },
    { id: "client", label: "Client", field: "clientName" },
    { id: "jobType", label: "Job Type", field: "jobType" },
    { id: "dueDate", label: "Due Date", field: "dueDate", type: "date" },
    { id: "status", label: "Status", field: "status", type: "status" },
    {
      id: "assignedTo",
      label: "Assigned To",
      render: (job) => (
        <Box>
          <Typography variant="body2">
            {job.assignedToName || "Unassigned"}
          </Typography>
          {job.secondaryUser && job.secondaryUser.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              + {job.secondaryUser.map((u) => u.name).join(", ")}
            </Typography>
          )}
        </Box>
      ),
    },
    { id: "createdAt", label: "Created At", field: "createdAt", type: "date" },
  ]

  // Define actions
  const actions = [
    {
      id: "view",
      label: "View Details",
      icon: <VisibilityIcon />,
    },
    {
      id: "edit",
      label: "Edit Job",
      icon: <EditIcon />,
    },
    {
      id: "addComment",
      label: "Add Comment",
      icon: <CommentIcon />,
    },
  ]

  const handleActionClick = (action, job) => {
    setSelectedJob(job)

    switch (action.id) {
      case "view":
        setViewDialogOpen(true)
        break
      case "addComment":
        setAddCommentDialogOpen(true)
        break
      case "edit":
        setEditDialogOpen(true)
        break
      default:
        break
    }
  }

  const handleEditSave = async (editData) => {
    try {
      if (!editData.JobId) {
        console.error("No job ID available for update")
        return
      }

      // console.log("handleEditSave editData", editData)

      // console.log("handleEditSave selectedJob", selectedJob)

      await updateJobMutation.mutateAsync({
        data: {
          fees: editData.fees,
          dueDate: editData.dueDate,
          assignedTo: editData.assignedTo,
          status: editData.status,
          jobTypeId: editData.jobTypeId,
          secondaryUser: editData.secondaryUsers,
        },
      })
    } catch (error) {
      console.error("Failed to update job:", error)
    }
  }

  const handleAddCommentSave = async () => {
    try {
      if (!comment) return
      const data = { commentText: comment }
      await setJobCommentMutation.mutateAsync(data)
    } catch (error) {
      console.log("Failed to add comment ", error)
    }
  }

  const handleExportToExcel = async () => {
    setIsExporting(true)
    try {
      // Build export params — same filters as current view but no page/limit
      const exportParams = new URLSearchParams()
      if (debouncedFilters.status !== "All") exportParams.set("status", debouncedFilters.status)
      if (debouncedFilters.subStatus !== "All") exportParams.set("subStatus", debouncedFilters.subStatus)
      if (debouncedFilters.assignedTo !== "All") exportParams.set("assignedTo", debouncedFilters.assignedTo)
      if (debouncedFilters.jobType !== "All") exportParams.set("jobType", debouncedFilters.jobType)
      if (debouncedFilters.jobId) exportParams.set("jobId", debouncedFilters.jobId)
      if (debouncedFilters.clientName) exportParams.set("clientName", debouncedFilters.clientName)
      if (debouncedFilters.clientPan) exportParams.set("clientPan", debouncedFilters.clientPan)
      if (debouncedSearch) exportParams.set("search", debouncedSearch)

      const response = await api.get(`/jobs/export?${exportParams.toString()}`)
      const exportJobs = response.data

      const excelData = exportJobs.map((job) => ({
        "Job ID": job.JobId,
        "Client Name": job.clientName,
        "Client Ph": job.clientPhoneNumber || "N/A",
        "Job Type": job.jobType,
        "Assigned To": job.assignedToName || "Unassigned",
        "Job Status": job.status,
        "Sub Status": job.subStatus ? getSubStatusLabel(job.subStatus) : "N/A",
        "Created At": job.createdAt ? new Date(job.createdAt).toLocaleDateString() : "N/A",
        "Due Date": job.dueDate ? new Date(job.dueDate).toLocaleDateString() : "N/A",
        "Fees (₹)": job.fees ?? 0,
        "Fee Status": job.feeStatus || "N/A",
      }))

      const worksheet = XLSX.utils.json_to_sheet(excelData)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, "Jobs")

      const columnWidths = [
        { wch: 15 }, // Job ID
        { wch: 25 }, // Client Name
        { wch: 15 }, // Client Ph
        { wch: 25 }, // Job Type
        { wch: 22 }, // Assigned To
        { wch: 14 }, // Job Status
        { wch: 22 }, // Sub Status
        { wch: 14 }, // Created At
        { wch: 14 }, // Due Date
        { wch: 12 }, // Fees
        { wch: 14 }, // Fee Status
      ]
      worksheet["!cols"] = columnWidths

      const fileName = `Jobs_${new Date().toISOString().split("T")[0]}.xlsx`
      XLSX.writeFile(workbook, fileName)
    } catch (error) {
      console.error("Export failed:", error)
    } finally {
      setIsExporting(false)
    }
  }

  if (isLoading) return <Typography>Loading...</Typography>

  const getPageTitle = () => {
    switch (initialStatus) {
      case "new":
        return "New Jobs"
      case "processing":
        return "Processing Jobs"
      case "completed":
        return "Completed Jobs"
      case "cancelled":
        return "Cancelled Jobs"
      default:
        return "All Jobs"
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          {getPageTitle()}
        </Typography>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            variant="outlined"
            startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExportToExcel}
            disabled={isExporting || totalJobs === 0}
          >
            {isExporting ? "Exporting..." : "Export to Excel"}
          </Button>
          <Button
            component={Link}
            variant="contained"
            startIcon={<AddIcon />}
            to="/add-job"
          >
            Create New Job
          </Button>
        </Box>
      </Box>

      {/* Search + Filter */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ p: 2 }}>
          {/* Quick Search Bar */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <TextField
              placeholder="Quick search by ID, client, type, or assignee..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
              size="small"
            />
            <Button
              variant={showAdvancedFilters ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              endIcon={
                getActiveFilterCount() > 0 && (
                  <Chip
                    label={getActiveFilterCount()}
                    size="small"
                    color="primary"
                    sx={{ height: 20, ml: -0.5 }}
                  />
                )
              }
            >
              Filters
            </Button>
            {getActiveFilterCount() > 0 && (
              <Button
                variant="text"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                color="error"
              >
                Clear All
              </Button>
            )}
          </Box>

           {/* Advanced Filters */}
          <Collapse in={showAdvancedFilters}>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {initialStatus === "All" && (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange("status", e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="All">All Status</MenuItem>
                      {STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sub-Status</InputLabel>
                  <Select
                    value={filters.subStatus}
                    onChange={(e) =>
                      handleFilterChange("subStatus", e.target.value)
                    }
                    label="Sub-Status"
                  >
                    <MenuItem value="All">All Sub-Status</MenuItem>
                    {SUB_STATUSES.map((subStatus) => (
                      <MenuItem key={subStatus} value={subStatus}>
                        {getSubStatusLabel(subStatus)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={filters.assignedTo}
                    onChange={(e) =>
                      handleFilterChange("assignedTo", e.target.value)
                    }
                    label="Assigned To"
                  >
                    <MenuItem value="All">All Users</MenuItem>
                    {users?.map((user) => (
                      <MenuItem key={user._id} value={user._id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Job Type</InputLabel>
                  <Select
                    value={filters.jobType}
                    onChange={(e) =>
                      handleFilterChange("jobType", e.target.value)
                    }
                    label="Job Type"
                  >
                    <MenuItem value="All">All Job Types</MenuItem>
                    {jobList?.map((job) => (
                      <MenuItem key={job._id} value={job._id}>
                        {job.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Job ID"
                  placeholder="Search by Job ID"
                  value={filters.jobId}
                  onChange={(e) => handleFilterChange("jobId", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Client Name"
                  placeholder="Search by client name"
                  value={filters.clientName}
                  onChange={(e) =>
                    handleFilterChange("clientName", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Client PAN"
                  placeholder="Search by PAN"
                  value={filters.clientPan}
                  onChange={(e) =>
                    handleFilterChange("clientPan", e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </Collapse>

          {/* Active Filters Display */}
          {getActiveFilterCount() > 0 && (
            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ alignSelf: "center" }}
              >
                Active filters:
              </Typography>
              {filters.status !== "All" && initialStatus === "All" && (
                <Chip
                  label={`Status: ${filters.status}`}
                  size="small"
                  onDelete={() => handleFilterChange("status", "All")}
                />
              )}
              {filters.subStatus !== "All" && (
                <Chip
                  label={`Sub-Status: ${getSubStatusLabel(filters.subStatus)}`}
                  size="small"
                  onDelete={() => handleFilterChange("subStatus", "All")}
                />
              )}
              {filters.assignedTo !== "All" && (
                <Chip
                  label={`Assigned: ${users?.find((u) => u._id === filters.assignedTo)?.name
                    }`}
                  size="small"
                  onDelete={() => handleFilterChange("assignedTo", "All")}
                />
              )}
              {filters.jobType !== "All" && (
                <Chip
                  label={`Type: ${jobList?.find((j) => j._id === filters.jobType)?.name
                    }`}
                  size="small"
                  onDelete={() => handleFilterChange("jobType", "All")}
                />
              )}
              {filters.jobId && (
                <Chip
                  label={`Job ID: ${filters.jobId}`}
                  size="small"
                  onDelete={() => handleFilterChange("jobId", "")}
                />
              )}
              {filters.clientName && (
                <Chip
                  label={`Client: ${filters.clientName}`}
                  size="small"
                  onDelete={() => handleFilterChange("clientName", "")}
                />
              )}
              {filters.clientPan && (
                <Chip
                  label={`PAN: ${filters.clientPan}`}
                  size="small"
                  onDelete={() => handleFilterChange("clientPan", "")}
                />
              )}
              {searchTerm && (
                <Chip
                  label={`Search: ${searchTerm}`}
                  size="small"
                  onDelete={() => setSearchTerm("")}
                />
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Results Summary */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {isLoading
            ? "Loading..."
            : `${totalJobs.toLocaleString()} job${totalJobs !== 1 ? "s" : ""} found`}
        </Typography>
      </Box>

      {/* Jobs Table - Reusable Component */}
      <JobsTable
        jobs={jobs}
        columns={columns}
        actions={actions}
        onActionClick={handleActionClick}
        emptyMessage="No jobs found matching the selected filters"
        serverSidePagination={true}
        totalCount={totalJobs}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10))
          setPage(0)
        }}
      />

      {/* View Details Dialog - Reusable Component */}
      <JobDetailsDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false)
          setSelectedJob(null)
        }}
        job={selectedJob}
      />

      {/* Edit Job Dialog */}
      {/* <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Job Details</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Fees"
              type="number"
              value={editData.fees}
              onChange={(e) => handleEditChange("fees", e.target.value)}
              fullWidth
            />
            <TextField
              label="Due Date"
              type="date"
              value={editData.dueDate}
              onChange={(e) => handleEditChange("dueDate", e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split("T")[0] }}
            />
            <FormControl fullWidth>
              <InputLabel>Job Type</InputLabel>
              <Select
                value={editData.jobTypeId}
                onChange={(e) => handleEditChange("jobTypeId", e.target.value)}
                label="Job Type"
              >
                {jobList?.map((job) => (
                  <MenuItem key={job._id} value={job._id}>
                    {job.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Assigned To</InputLabel>
              <Select
                value={editData.assignedTo}
                onChange={(e) => handleEditChange("assignedTo", e.target.value)}
                label="Assigned To"
              >
                {users?.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog> */}
      <EditJobDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        job={selectedJob}
        users={users}
        jobTypes={jobList}
        onSave={handleEditSave}
      />
      {/* Add Comment Dialog */}
      <Dialog
        open={addCommentDialogOpen}
        onClose={() => setAddCommentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedJob.JobId} - {selectedJob.jobType}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Client: {selectedJob.clientName}
              </Typography>
              <TextField
                fullWidth
                label="Comments"
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add notes about the update..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCommentDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCommentSave} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default JobsPage